import { checkpointHostAlert } from '../vm/checkpoint-status.js';
/**
 * Host Resource Monitoring
 *
 * Renders metric bars dynamically from the server's metrics array.
 * Each metric has a machine-readable name, human description, value, max, and unit.
 *
 * Supports both the local host and remote (delegated) hosts.  Each host gets
 * its own fieldset identified by a data-host-monitor attribute.  Metrics for
 * every host arrive on the page's /ws/events connection (#186): this host
 * samples its own and relays its peers'.
 */

import type { HostMetrics, HostMetric, HostAttribute } from '../api/types.js';
import { connectEvents, type EventsConnection } from '../api/events-ws.js';
import { escapeHtml } from '../utils.js';

let resourceCallsDisabled = false;

const HOST_MONITOR_ATTR = 'data-host-monitor';

export function setResourceCallsDisabled(disabled: boolean): void {
  resourceCallsDisabled = disabled;
  if (disabled) {
    disconnectHostMonitor();
  }
}

/**
 * Format a metric value for display.
 * Rounds to 1 decimal place for fractional values.
 */
function formatValue(v: number): string {
  return v % 1 === 0 ? String(v) : v.toFixed(1);
}

/**
 * Render a single metric's info + bar elements.
 * Uses the metric name as a CSS class for targeting updates.
 */
function renderMetricHtml(metric: HostMetric): string {
  const cls = escapeHtml(metric.name);
  return `<div class="metric-info metric-info-${cls}"></div>` +
         `<div class="metric-bar metric-bar-${cls}"></div>`;
}

/**
 * Render a single attribute as a text line.
 */
function renderAttributeHtml(attr: HostAttribute): string {
  const cls = escapeHtml(attr.name);
  return `<div class="host-attr host-attr-${cls}"></div>`;
}

/** Names of the three load average metrics from the server */
const LOAD_NAMES = ['load_1min', 'load_5min', 'load_15min'] as const;

/** Check whether a metric name is one of the load averages */
function isLoadMetric(name: string): boolean {
  return (LOAD_NAMES as readonly string[]).includes(name);
}

/**
 * Render the combined load average bar HTML.
 */
function renderLoadBarHtml(): string {
  return `<div class="metric-info metric-info-load_avg"></div>` +
         `<div class="load-bar metric-bar-load_avg">` +
           `<div class="load-bar-layer load-bar-15min"></div>` +
           `<div class="load-bar-layer load-bar-5min"></div>` +
           `<div class="load-bar-layer load-bar-1min"></div>` +
         `</div>`;
}

/**
 * Update the resource bars for a specific host.
 * Creates the container and bars on first call; updates in place on subsequent calls.
 *
 * @param data - Metrics payload from the WebSocket
 * @param hostKey - Unique key for this host ("" for local, host name for remote)
 * @param legendText - Legend text for the fieldset
 *
 * The three load_* metrics are consolidated into a single 3-layer stacked bar
 * instead of rendering three separate progress bars.
 */
function updateMetrics(data: HostMetrics, hostKey: string, legendText: string): void {
  const $area = $('#resource-area');
  const selector = `[${HOST_MONITOR_ATTR}="${escapeHtml(hostKey)}"]`;
  let $container = $area.find(selector);

  // Separate load metrics from regular metrics
  const regularMetrics = data.metrics.filter(m => !isLoadMetric(m.name));
  const loadMetrics: Record<string, HostMetric> = {};
  for (const m of data.metrics) {
    if (isLoadMetric(m.name)) loadMetrics[m.name] = m;
  }
  const hasLoad = Object.keys(loadMetrics).length > 0;

  // Create container on first update
  if ($container.length === 0) {
    const barsHtml = regularMetrics.map(renderMetricHtml).join('');
    const loadHtml = hasLoad ? renderLoadBarHtml() : '';
    const attrsHtml = (data.attributes || []).map(renderAttributeHtml).join('');
    $area.append(`
      <fieldset class="host-resources" ${HOST_MONITOR_ATTR}="${escapeHtml(hostKey)}">
        <legend>${escapeHtml(legendText)}</legend>
        <div class="checkpoint-alert ui-state-highlight" role="alert" hidden></div>
        <div class="host-attributes">${attrsHtml}</div>
        ${barsHtml}
        ${loadHtml}
      </fieldset>
      <br/>
    `);
    $container = $area.find(selector);

    // Initialize jQuery UI progressbars for regular metrics
    for (const m of regularMetrics) {
      $container.find(`.metric-bar-${m.name}`).progressbar();
    }
  }

  const checkpointAlert = checkpointHostAlert(data.checkpoint_past_max_age, data.checkpoint_low_space);
  $container.find('.checkpoint-alert').text(checkpointAlert ?? '').prop('hidden', checkpointAlert === null);

  // Update attributes
  const $attrs = $container.find('.host-attributes');
  for (const a of (data.attributes || [])) {
    let $attr = $attrs.find(`.host-attr-${a.name}`);
    if ($attr.length === 0) {
      $attrs.append(renderAttributeHtml(a));
      $attr = $attrs.find(`.host-attr-${a.name}`);
    }
    $attr.html(`<span class='caption'>${escapeHtml(a.description)}: ${escapeHtml(a.value)}</span>`);
  }

  // Update regular metrics
  for (const m of regularMetrics) {
    let $bar = $container.find(`.metric-bar-${m.name}`);
    if ($bar.length === 0) {
      $container.append(renderMetricHtml(m));
      $bar = $container.find(`.metric-bar-${m.name}`);
      $bar.progressbar();
    }

    const percent = m.max > 0 ? (m.value / m.max) * 100 : 0;
    $bar.progressbar('option', 'value', percent);

    const caption = m.unit
      ? `${formatValue(m.value)} / ${formatValue(m.max)} ${m.unit}`
      : `${formatValue(m.value)} / ${formatValue(m.max)}`;
    $container
      .find(`.metric-info-${m.name}`)
      .html(`<span class='caption'>${escapeHtml(m.description)}: ${caption}</span>`);
  }

  // Update combined load bar
  if (hasLoad) {
    // Ensure load bar exists
    if ($container.find('.metric-bar-load_avg').length === 0) {
      $container.append(renderLoadBarHtml());
    }

    const load1 = loadMetrics.load_1min;
    const load5 = loadMetrics.load_5min;
    const load15 = loadMetrics.load_15min;
    const max = load1?.max || load5?.max || load15?.max || 1;

    // Update each layer's width
    if (load15) $container.find('.load-bar-15min').css('width', `${(load15.value / max) * 100}%`);
    if (load5)  $container.find('.load-bar-5min').css('width', `${(load5.value / max) * 100}%`);
    if (load1)  $container.find('.load-bar-1min').css('width', `${(load1.value / max) * 100}%`);

    // Caption: "Load Average: 0.0 / 0.0 / 0.1 (2 cores)"
    const v1 = load1 ? formatValue(load1.value) : '-';
    const v5 = load5 ? formatValue(load5.value) : '-';
    const v15 = load15 ? formatValue(load15.value) : '-';
    $container
      .find('.metric-info-load_avg')
      .html(`<span class='caption'>Load Average: ${v1} / ${v5} / ${v15} (${formatValue(max)} cores)</span>`);
  }
}

// ============================================================================
// Host metrics from /ws/events (#186): the edge samples its own host and
// relays its peers', so one socket replaces a polled socket per host.
// ============================================================================

let metricsEvents: EventsConnection | null = null;

/**
 * Show resource bars for `remoteHosts` and, if `showLocal`, for this host.
 * Metrics for any other peer are ignored.
 */
export function connectHostMetricsEvents(showLocal: boolean, remoteHosts: Set<string>): void {
  if (resourceCallsDisabled || metricsEvents) return;
  metricsEvents = connectEvents(['host_metrics'], {
    onHostMetrics: (host, data, isLocal) => {
      if (isLocal) {
        if (showLocal) updateMetrics(data as unknown as HostMetrics, '', 'Host');
      } else if (remoteHosts.has(host)) {
        updateMetrics(data as unknown as HostMetrics, host, host);
      }
    },
  });
}

export function disconnectHostMonitor(): void {
  metricsEvents?.close();
  metricsEvents = null;
}

// Export to window for onclick handlers
if (typeof window !== 'undefined') {
  (window as unknown as Record<string, unknown>).disconnectHostMonitor = disconnectHostMonitor;
}
